import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../../shared/lib/supabase';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import Toast from '../../../shared/components/ui/Toast';
import SearchableDropdown from '../../../shared/components/ui/SearchableDropdown';

const TeamSquadPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const orgIdFromPath = extractOrgIdFromPath(location.pathname);

  // State
  const [teamDetails, setTeamDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [players, setPlayers] = useState<any[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(new Set());
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSearchTerm, setDrawerSearchTerm] = useState('');
  const [currentSquad, setCurrentSquad] = useState<any[]>([]);
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [seasons, setSeasons] = useState<any[]>([]);
  const [squadAssignments, setSquadAssignments] = useState<Array<{
    tournamentId: string;
    seasonId: string;
    tournamentName: string;
    seasonName: string;
  }>>([]);
  const [tempTournament, setTempTournament] = useState<string>('');
  const [tempSeason, setTempSeason] = useState<string>('');
  
  // Main page filters
  const [mainPageTournament, setMainPageTournament] = useState<string>('');
  const [mainPageSeason, setMainPageSeason] = useState<string>('');
  
  // Edit Squad Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalTournament, setEditModalTournament] = useState<string>('');
  const [editModalSeason, setEditModalSeason] = useState<string>('');
  const [editSquadData, setEditSquadData] = useState<any[]>([]);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Map<string, boolean>>(new Map());
  
  // Toast state
  const [toast, setToast] = useState<{isVisible: boolean, type: 'success' | 'error' | 'info', message: string}>({
    isVisible: false,
    type: 'success',
    message: ''
  });

  useEffect(() => {
    const loadTeamData = async () => {
      if (!teamId || !orgIdFromPath) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Get team details from team_access
        const teamResult = await supabase
          .from('team_access')
          .select('team:team_id(id, name, logo, city, country, venue, website, social_media)')
          .eq('team_id', teamId)
          .eq('organisation_id', orgIdFromPath)
          .eq('active', true)
          .single();

        if (teamResult.error) {
          console.error('Error fetching team details:', teamResult.error);
          setError('Failed to load team details');
          return;
        }

        // Extract team data from team_access result
        const teamData = teamResult.data?.team || null;
        setTeamDetails(teamData);
        
        // Load current squad, tournaments, and seasons
        await Promise.all([
          loadCurrentSquad(),
          loadTournaments(),
          loadSeasons()
        ]);
        
      } catch (error) {
        console.error('Error loading team data:', error);
        setError('Failed to load team data');
      } finally {
        setLoading(false);
      }
    };

    loadTeamData();
  }, [teamId, orgIdFromPath]);

  // Load current squad players
  const loadCurrentSquad = async () => {
    if (!teamId) return;
    
    try {
      const squadResult = await supabase
        .from('team_squad')
        .select(`
          id,
          team_id,
          player_id,
          tournament_id,
          season_id,
          kit_number,
          position,
          is_active,
          player:player_id(
            id,
            full_name,
            position,
            nationality,
            profile_image,
            date_of_birth,
            kit_number,
            height_cm,
            weight_kg
          ),
          tournament:tournament_id(
            id,
            name
          ),
          season:season_id(
            id,
            name,
            start_date,
            end_date
          )
        `)
        .eq('team_id', teamId)
        .eq('is_active', true);

      console.log('Squad result:', squadResult);
      
      if (squadResult.data) {
        setCurrentSquad(squadResult.data);
        console.log('Squad data loaded:', squadResult.data);
        // Debug individual player data
        squadResult.data.forEach((squad: any, index: number) => {
          console.log(`Player ${index + 1}:`, {
            name: squad.player?.full_name,
            position: squad.player?.position,
            nationality: squad.player?.nationality,
            kit_number: squad.player?.kit_number,
            height_cm: squad.player?.height_cm,
            weight_kg: squad.player?.weight_kg
          });
        });
      } else {
        console.log('No squad data found');
      }
    } catch (error) {
      console.error('Error loading current squad:', error);
    }
  };

  // Load tournaments from tournament_access (using same approach as SummaryPage)
  const loadTournaments = async () => {
    if (!orgIdFromPath) return;
    
    try {
      const tournamentAccessResult = await supabase
        .from('tournament_access')
        .select('id, access_type, granted_at, is_active, tournament:tournament_id(id, name, logo, country)')
        .eq('organisation_id', orgIdFromPath)
        .eq('is_active', true);

      console.log('Tournament access result:', tournamentAccessResult);
      
      if (tournamentAccessResult.data) {
        const tournamentData = tournamentAccessResult.data.map((t: any) => t.tournament).filter(Boolean);
        console.log('Tournament access data:', tournamentAccessResult.data);
        console.log('Tournament data:', tournamentData);
        setTournaments(tournamentData);
      } else {
        console.log('No tournament access data found');
      }
    } catch (error) {
      console.error('Error loading tournaments:', error);
    }
  };

  // Load seasons from season_access (using same approach as SummaryPage)
  const loadSeasons = async () => {
    if (!orgIdFromPath) return;
    
    try {
      const seasonAccessResult = await supabase
        .from('season_access')
        .select('id, access_type, granted_at, active, season:season_id(id, name, start_date, end_date)')
        .eq('organisation_id', orgIdFromPath)
        .eq('active', true);

      console.log('Season access result:', seasonAccessResult);
      
      if (seasonAccessResult.data) {
        const seasonData = seasonAccessResult.data.map((s: any) => s.season).filter(Boolean);
        console.log('Season access data:', seasonAccessResult.data);
        console.log('Season data:', seasonData);
        setSeasons(seasonData);
      } else {
        console.log('No season access data found');
      }
    } catch (error) {
      console.error('Error loading seasons:', error);
    }
  };

  // Cleanup effect to restore scroll when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Load players from player_access (using same approach as SummaryPage)
  const loadPlayers = async () => {
    if (!orgIdFromPath) return;
    
    try {
      setDrawerLoading(true);
      console.log('Loading players for orgId:', orgIdFromPath);
      
      // Use the same query structure as SummaryPage
      const playerAccessResult = await supabase
        .from('player_access')
        .select('id, access_type, granted_at, active, player:player_id(id, full_name, position, nationality, kit_number, bio, profile_image, date_of_birth, social_media, height_cm, weight_kg)')
        .eq('organisation_id', orgIdFromPath)
        .eq('active', true);

      console.log('Player access result:', playerAccessResult);

      if (playerAccessResult.error) {
        console.error('Error fetching player access:', playerAccessResult.error);
        setPlayers([]);
        return;
      }

      if (playerAccessResult.data && playerAccessResult.data.length > 0) {
        // Extract player data from the nested structure (same as SummaryPage)
        const playerData = playerAccessResult.data.map((pa: any) => pa.player).filter(Boolean);
        console.log('Extracted player data:', playerData);
        setPlayers(playerData);
      } else {
        console.log('No player access records found');
        setPlayers([]);
      }
    } catch (error) {
      console.error('Error loading players:', error);
      setPlayers([]);
    } finally {
      setDrawerLoading(false);
    }
  };

  // Handle drawer open
  const handleManageSquad = () => {
    setIsDrawerOpen(true);
    // Disable body scroll
    document.body.style.overflow = 'hidden';
    loadPlayers();
    // Reset squad assignments
    setSquadAssignments([]);
    setTempTournament('');
    setTempSeason('');
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    // Re-enable body scroll
    document.body.style.overflow = 'unset';
    // Reset squad assignments
    setSquadAssignments([]);
    setTempTournament('');
    setTempSeason('');
    setSelectedPlayers(new Set());
  };

  // Handle adding a tournament/season assignment
  const handleAddSquadAssignment = () => {
    if (!tempTournament || !tempSeason) {
      return;
    }

    const tournament = tournaments.find(t => t.id === tempTournament);
    const season = seasons.find(s => s.id === tempSeason);

    if (!tournament || !season) {
      return;
    }

    // Check if this combination already exists
    const exists = squadAssignments.some(
      sa => sa.tournamentId === tempTournament && sa.seasonId === tempSeason
    );

    if (exists) {
      return;
    }

    setSquadAssignments(prev => [...prev, {
      tournamentId: tempTournament,
      seasonId: tempSeason,
      tournamentName: tournament.name,
      seasonName: season.name
    }]);

    setTempTournament('');
    setTempSeason('');
  };

  // Handle removing a tournament/season assignment
  const handleRemoveSquadAssignment = (index: number) => {
    setSquadAssignments(prev => prev.filter((_, i) => i !== index));
  };

  // Save selected players to team_squads table
  const handleSaveChanges = async () => {
    if (squadAssignments.length === 0 || selectedPlayers.size === 0) {
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Please add at least one tournament/season combination and select at least one player'
      });
      return;
    }

    if (!teamId) {
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Team ID not found'
      });
      return;
    }

    try {
      const playerIds = Array.from(selectedPlayers);
      let successCount = 0;
      let updateCount = 0;
      let insertCount = 0;

      // Process each player and each assignment combination
      for (const playerId of playerIds) {
        for (const assignment of squadAssignments) {
          // First, check if a record already exists for this player, team, tournament, and season
          const { data: existingRecord, error: checkError } = await supabase
            .from('team_squad')
            .select('id, is_active')
            .eq('team_id', teamId)
            .eq('player_id', playerId)
            .eq('tournament_id', assignment.tournamentId)
            .eq('season_id', assignment.seasonId)
            .single();

          if (checkError && checkError.code !== 'PGRST116') {
            // PGRST116 is "not found" error, which is expected for new records
            console.error('Error checking existing record:', checkError);
            continue;
          }

          if (existingRecord) {
            // Record exists - update is_active to true
            const { error: updateError } = await supabase
              .from('team_squad')
              .update({ 
                is_active: true,
                updated_at: new Date().toISOString()
              })
              .eq('id', existingRecord.id);

            if (updateError) {
              console.error('Error updating existing record:', updateError);
            } else {
              updateCount++;
              successCount++;
            }
          } else {
            // Record doesn't exist - insert new record
            const { error: insertError } = await supabase
              .from('team_squad')
              .insert({
                team_id: teamId,
                player_id: playerId,
                tournament_id: assignment.tournamentId,
                season_id: assignment.seasonId,
                kit_number: null,
                is_active: true
              });

            if (insertError) {
              console.error('Error inserting new record:', insertError);
            } else {
              insertCount++;
              successCount++;
            }
          }
        }
      }

      // Success - close drawer and refresh squad
      handleDrawerClose();
      
      // Reload current squad to show updated data
      await loadCurrentSquad();
      
      // Show success message with details
      let successMessage = `${successCount} player assignments successfully added to squad!`;
      if (updateCount > 0 && insertCount > 0) {
        successMessage = `${successCount} assignments added (${insertCount} new, ${updateCount} reactivated)!`;
      } else if (updateCount > 0) {
        successMessage = `${updateCount} assignments reactivated in squad!`;
      } else if (insertCount > 0) {
        successMessage = `${insertCount} assignments added to squad!`;
      }
      
      setToast({
        isVisible: true,
        type: 'success',
        message: successMessage
      });
    } catch (error: any) {
      console.error('Error saving squad:', error);
      
      // Provide specific error messages based on the error type
      let errorMessage = 'Failed to save squad. Please try again.';
      
      if (error?.code === '23503') {
        // Foreign key constraint violation
        errorMessage = 'Invalid tournament or season selected. Please refresh and try again.';
      } else if (error?.code === '23502') {
        // Not null constraint violation
        errorMessage = 'Missing required information. Please ensure all fields are selected.';
      } else if (error?.message) {
        // Use the actual error message if available
        errorMessage = error.message;
      }
      
      setToast({
        isVisible: true,
        type: 'error',
        message: errorMessage
      });
    }
  };

  // Handle Edit Squad Modal
  const handleEditSquad = () => {
    setIsEditModalOpen(true);
    // Load all squad data for the team
    loadEditSquadData();
  };

  // Load squad data for edit modal
  const loadEditSquadData = async () => {
    if (!teamId) return;
    
    setEditModalLoading(true);
    try {
      const squadResult = await supabase
        .from('team_squad')
        .select(`
          id,
          player_id,
          tournament_id,
          season_id,
          kit_number,
          position,
          is_active,
          created_at,
          updated_at,
          data_source,
          player:player_id(
            id,
            full_name,
            position,
            nationality,
            profile_image,
            date_of_birth,
            kit_number,
            height_cm,
            weight_kg
          ),
          tournament:tournament_id(
            id,
            name
          ),
          season:season_id(
            id,
            name,
            start_date,
            end_date
          )
        `)
        .eq('team_id', teamId);

      if (squadResult.data) {
        setEditSquadData(squadResult.data);
      }
    } catch (error) {
      console.error('Error loading edit squad data:', error);
    } finally {
      setEditModalLoading(false);
    }
  };

  // Filter edit squad data based on tournament and season
  const getFilteredEditSquadData = () => {
    return editSquadData.filter(squad => {
      const matchesTournament = !editModalTournament || squad.tournament_id === editModalTournament;
      const matchesSeason = !editModalSeason || squad.season_id === editModalSeason;
      return matchesTournament && matchesSeason;
    });
  };

  // Handle active status toggle (track changes for batch save)
  const handleActiveToggle = (squadId: string, currentStatus: boolean) => {
    const newStatus = !currentStatus;
    setPendingChanges(prev => new Map(prev.set(squadId, newStatus)));
    
    // Update local state for immediate UI feedback
    setEditSquadData(prev => 
      prev.map(squad => 
        squad.id === squadId 
          ? { ...squad, is_active: newStatus }
          : squad
      )
    );
  };

  // Save all pending changes
  const handleSaveEditChanges = async () => {
    if (pendingChanges.size === 0) {
      setToast({
        isVisible: true,
        type: 'info',
        message: 'No changes to save'
      });
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      // Process each change
      for (const [squadId, newStatus] of pendingChanges) {
        const { error } = await supabase
          .from('team_squad')
          .update({ 
            is_active: newStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', squadId);

        if (error) {
          console.error('Error updating squad:', error);
          errorCount++;
        } else {
          successCount++;
        }
      }

      // Clear pending changes
      setPendingChanges(new Map());
      
      // Reload current squad to show updated data
      await loadCurrentSquad();

      if (errorCount > 0) {
        setToast({
          isVisible: true,
          type: 'error',
          message: `${successCount} players updated successfully, ${errorCount} failed`
        });
      } else {
        setToast({
          isVisible: true,
          type: 'success',
          message: `${successCount} players updated successfully`
        });
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to save changes'
      });
    }
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditModalTournament('');
    setEditModalSeason('');
    setPendingChanges(new Map());
  };

  // Handle player selection
  const handlePlayerSelect = (playerId: string) => {
    const newSelected = new Set(selectedPlayers);
    if (newSelected.has(playerId)) {
      newSelected.delete(playerId);
    } else {
      newSelected.add(playerId);
    }
    setSelectedPlayers(newSelected);
  };

  // Filter players based on search term
  const filteredPlayers = players.filter(player => {
    if (!drawerSearchTerm) return true;
    const searchLower = drawerSearchTerm.toLowerCase();
    return (
      player.full_name?.toLowerCase().includes(searchLower) ||
      player.position?.toLowerCase().includes(searchLower) ||
      player.nationality?.toLowerCase().includes(searchLower) ||
      player.kit_number?.toString().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading team details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Team</h3>
          <p className="text-sm text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/o/${orgIdFromPath}/roster`)}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Teams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left: Back Button */}
          <button
            onClick={() => navigate(`/o/${orgIdFromPath}/roster`)}
            className="flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Teams
          </button>

          {/* Center: Search Bar */}
          <div className="flex-1 max-w-md mx-8">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search players..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Right: Action Buttons */}
          <div className="flex items-center space-x-3">
            {/* Edit Squad Button */}
            <button
              onClick={handleEditSquad}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Edit Squad
            </button>
            
            {/* Link Players Button */}
            <button
              onClick={handleManageSquad}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors space-x-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
              <span>Player</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="h-full flex flex-col">
        <main className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">
                  {teamDetails?.name ? `${teamDetails.name} Squad` : 'Team Squad'}
                </h1>
                
                {/* Filters */}
                <div className="flex items-center space-x-4">
                  {/* Tournament Filter */}
                  <div className="min-w-[200px]">
                    <SearchableDropdown
                      options={tournaments.map(t => ({ id: t.id, name: t.name }))}
                      value={mainPageTournament}
                      onChange={setMainPageTournament}
                      placeholder="All Tournaments"
                    />
                  </div>
                  
                  {/* Season Filter */}
                  <div className="min-w-[200px]">
                    <SearchableDropdown
                      options={seasons.map(s => ({ id: s.id, name: s.name }))}
                      value={mainPageSeason}
                      onChange={setMainPageSeason}
                      placeholder="All Seasons"
                    />
                  </div>
                </div>
              </div>
            </div>

             {/* Squad Players */}
             {loading ? (
               <div className="flex justify-center items-center py-16">
                 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                 <span className="ml-3 text-gray-600">Loading squad...</span>
               </div>
             ) : currentSquad.length > 0 ? (
               <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                
           <div className="p-6">
             {(() => {
               const filteredSquad = currentSquad.filter(squad => {
                 // Search term filter
                 const matchesSearch = !searchTerm || 
                   squad.player?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   squad.player?.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   squad.player?.nationality?.toLowerCase().includes(searchTerm.toLowerCase());
                 
                 // Tournament filter
                 const matchesTournament = !mainPageTournament || squad.tournament_id === mainPageTournament;
                 
                 // Season filter
                 const matchesSeason = !mainPageSeason || squad.season_id === mainPageSeason;
                 
                 return matchesSearch && matchesTournament && matchesSeason;
               });

               // Group players by position
               const positionGroups = {
                 'Goalkeeper': filteredSquad.filter(squad => 
                   squad.player?.position?.toLowerCase().includes('goalkeeper') || 
                   squad.player?.position?.toLowerCase().includes('keeper')
                 ),
                 'Defender': filteredSquad.filter(squad => 
                   squad.player?.position?.toLowerCase().includes('defender') || 
                   squad.player?.position?.toLowerCase().includes('defence') ||
                   squad.player?.position?.toLowerCase().includes('back')
                 ),
                 'Midfielder': filteredSquad.filter(squad => 
                   squad.player?.position?.toLowerCase().includes('midfielder') || 
                   squad.player?.position?.toLowerCase().includes('midfield') ||
                   squad.player?.position?.toLowerCase().includes('mid')
                 ),
                 'Forward': filteredSquad.filter(squad => 
                   squad.player?.position?.toLowerCase().includes('forward') || 
                   squad.player?.position?.toLowerCase().includes('striker') ||
                   squad.player?.position?.toLowerCase().includes('attacker') ||
                   squad.player?.position?.toLowerCase().includes('winger')
                 )
               };


               const getPositionIcon = (position: string) => {
                 const pos = position.toLowerCase();
                 if (pos.includes('goalkeeper') || pos.includes('keeper')) return '🥅';
                 if (pos.includes('defender') || pos.includes('defence') || pos.includes('back')) return '🛡️';
                 if (pos.includes('midfielder') || pos.includes('midfield') || pos.includes('mid')) return '⚽';
                 if (pos.includes('forward') || pos.includes('striker') || pos.includes('attacker') || pos.includes('winger')) return '🎯';
                 return '⚽';
               };

               return (
                 <div className="space-y-8">
                   {Object.entries(positionGroups).map(([positionName, players]) => {
                     if (players.length === 0) return null;
                     
                     return (
                       <div key={positionName}>
                         <div className="flex items-center mb-4">
                           <span className="text-2xl mr-2">{getPositionIcon(positionName)}</span>
                           <h3 className="text-lg font-semibold text-gray-900">{positionName}s</h3>
                         </div>
                         
                         <div className="space-y-3">
                           {(() => {
                             // Group squad entries by player_id
                             const playerGroups = new Map<string, typeof players>();
                             players.forEach((squad) => {
                               const playerId = squad.player_id;
                               if (!playerGroups.has(playerId)) {
                                 playerGroups.set(playerId, []);
                               }
                               playerGroups.get(playerId)!.push(squad);
                             });

                             return Array.from(playerGroups.values()).map((playerSquads) => {
                               const firstSquad = playerSquads[0];
                               const player = firstSquad.player;
                               
                               // Filter assignments by current team and tournament filter
                               const filteredAssignments = playerSquads.filter(squad => {
                                 // Always filter by current team (teamId)
                                 const matchesTeam = squad.team_id === teamId;
                                 // Filter by tournament if selected
                                 const matchesTournament = !mainPageTournament || squad.tournament_id === mainPageTournament;
                                 return matchesTeam && matchesTournament;
                               });

                               if (filteredAssignments.length === 0) return null;

                               const getInitials = (name?: string) => {
                                 if (!name) return '??';
                                 const words = name.trim().split(/\s+/).filter(Boolean);
                                 if (words.length === 0) return '??';
                                 if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
                                 return (words[0][0] + words[1][0]).toUpperCase();
                               };

                               // Get kit number and position from squad (prefer squad-specific, fallback to player)
                               const kitNumber = filteredAssignments[0]?.kit_number || player?.kit_number;
                               const position = filteredAssignments[0]?.position || player?.position;

                               const summaryParts = [
                                 kitNumber ? `#${kitNumber}` : null,
                                 position || null,
                               ].filter(Boolean);

                               // Build assignment display - only show tournament and season for filtered assignments
                               const assignmentLabels = filteredAssignments.map((squad) => {
                                 const parts = [
                                   squad.tournament?.name || null,
                                   squad.season?.name || null,
                                 ].filter(Boolean);
                                 return parts.join(' • ');
                               }).filter(Boolean);

                               return (
                                 <div
                                   key={firstSquad.id}
                                   className="rounded-lg border border-gray-200 bg-white p-3 sm:p-3.5 shadow-sm shadow-transparent transition-shadow hover:shadow-md"
                                 >
                                   <div className="grid grid-cols-[auto,1fr] gap-2.5 sm:gap-3 items-center">
                                     <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 text-xs sm:text-sm font-semibold uppercase text-white">
                                       {getInitials(player?.full_name)}
                                     </div>
                                     <div className="space-y-1">
                                       <h3 className="text-sm sm:text-sm font-semibold text-gray-900">
                                         {player?.full_name || 'Unknown Player'}
                                       </h3>
                                       {summaryParts.length > 0 && (
                                         <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] sm:text-xs text-gray-600">
                                           {summaryParts.map((part, index) => (
                                             <React.Fragment key={`${firstSquad.id}-summary-${index}`}>
                                               {index > 0 && <span>•</span>}
                                               <span>{part}</span>
                                             </React.Fragment>
                                           ))}
                                         </div>
                                       )}
                                     </div>
                                   </div>
                                   
                                   {/* Assignment details */}
                                   {assignmentLabels.length > 0 && (
                                     <div className="mt-2.5 sm:mt-3 space-y-1.5">
                                       {assignmentLabels.map((label, index) => (
                                         <div
                                           key={`${firstSquad.id}-assignment-${index}`}
                                           className="rounded-md border border-gray-100 bg-gray-50 px-3 py-1.5"
                                         >
                                           <div className="text-[11px] sm:text-xs font-medium text-gray-700">
                                             {label}
                                           </div>
                                         </div>
                                       ))}
                                     </div>
                                   )}
                                 </div>
                               );
                             }).filter(Boolean);
                           })()}
                         </div>
                       </div>
                     );
                   })}
                 </div>
               );
             })()}
           </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Squad Players</h3>
                <p className="text-gray-500">Start adding players to build your squad</p>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Right Drawer */}
      {isDrawerOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={handleDrawerClose}
          />
          
          {/* Drawer */}
          <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col">
            {/* Drawer Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Link players to squad</h2>
                <button
                  onClick={handleDrawerClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Add Tournament/Season Assignments */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Add Tournament/Season Assignments
                </label>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {/* Tournament */}
                  <div>
                    <select
                      value={tempTournament}
                      onChange={(e) => setTempTournament(e.target.value)}
                      className={`block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                        !tempTournament ? 'text-gray-500' : 'text-gray-700'
                      }`}
                    >
                      <option value="" disabled className="text-gray-500">Tournament</option>
                      {tournaments.map((tournament) => (
                        <option key={tournament.id} value={tournament.id} className="text-gray-700">
                          {tournament.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Season */}
                  <div>
                    <select
                      value={tempSeason}
                      onChange={(e) => setTempSeason(e.target.value)}
                      className={`block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                        !tempSeason ? 'text-gray-500' : 'text-gray-700'
                      }`}
                    >
                      <option value="" disabled className="text-gray-500">Season</option>
                      {seasons.map((season) => (
                        <option key={season.id} value={season.id} className="text-gray-700">
                          {season.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Add Button */}
                  <div>
                    <button
                      type="button"
                      onClick={handleAddSquadAssignment}
                      className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                    >
                      + Add
                    </button>
                  </div>
                </div>
              </div>

              {/* List of Added Assignments */}
              {squadAssignments.length > 0 && (
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Added Assignments ({squadAssignments.length})
                  </label>
                  <div className="space-y-2">
                    {squadAssignments.map((assignment, index) => (
                      <div
                        key={`${assignment.tournamentId}-${assignment.seasonId}`}
                        className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md border border-gray-200"
                      >
                        <span className="text-xs text-gray-700">
                          {assignment.tournamentName} • {assignment.seasonName}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSquadAssignment(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Search Bar */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search players..."
                  value={drawerSearchTerm}
                  onChange={(e) => setDrawerSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {drawerLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading players...</span>
                </div>
              ) : filteredPlayers.length > 0 ? (
                <div className="space-y-3">
                  {filteredPlayers.map((player) => (
                    <div key={player.id} className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedPlayers.has(player.id)}
                        onChange={() => handlePlayerSelect(player.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                      />
                      
                      {/* Player Avatar */}
                      <div className="ml-3 mr-3 flex-shrink-0">
                        {player.profile_image ? (
                          <img
                            src={player.profile_image}
                            alt={player.full_name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {player.full_name?.charAt(0) || '?'}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Player Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {player.full_name || 'Unknown Player'}
                          </p>
                          <div className="flex items-center mt-1 space-x-2">
                            <p className="text-xs text-gray-500">{player.position || '—'}</p>
                            {/* Kit Number */}
                            {player.kit_number && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                #{player.kit_number}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Additional Details */}
                        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-600">
                          <div className="flex items-center">
                            <svg className="w-3 h-3 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5M3 17h9m9 0a2 2 0 002-2V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4m11 0l-3 3m0 0l-3-3m3 3v6m-9-9h.01" />
                            </svg>
                            <span>{player.nationality || '—'}</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-3 h-3 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>
                              {player.date_of_birth ? new Date(player.date_of_birth).toLocaleDateString('en-GB') : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">
                    {drawerSearchTerm ? 'No Players Found' : 'No Players Available'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {drawerSearchTerm 
                      ? `No players match "${drawerSearchTerm}". Try a different search term.`
                      : 'No players available for this organization.'
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            {players.length > 0 && (
              <div className="border-t border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {selectedPlayers.size} of {filteredPlayers.length} selected
                  </span>
                  <div className="space-x-2">
                    <button
                      onClick={handleDrawerClose}
                      className="px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveChanges}
                      className="px-3 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
        type={toast.type}
        message={toast.message}
        duration={6000}
      />

      {/* Edit Squad Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Edit Squad</h2>
                <button
                  onClick={handleCloseEditModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Filters */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tournament</label>
                  <SearchableDropdown
                    options={tournaments.map(t => ({ id: t.id, name: t.name }))}
                    value={editModalTournament}
                    onChange={setEditModalTournament}
                    placeholder="All Tournaments"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Season</label>
                  <SearchableDropdown
                    options={seasons.map(s => ({ id: s.id, name: s.name }))}
                    value={editModalSeason}
                    onChange={setEditModalSeason}
                    placeholder="All Seasons"
                  />
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {editModalLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading squad data...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {getFilteredEditSquadData().map((squad) => (
                    <div key={squad.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="grid grid-cols-12 gap-4 items-center">
                        {/* Player Info - 6 columns */}
                        <div className="col-span-6 flex items-center space-x-3">
                          {/* Player Avatar */}
                          <div className="w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center flex-shrink-0">
                            {squad.player?.profile_image ? (
                              <img
                                src={squad.player.profile_image}
                                alt={squad.player.full_name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-sm font-bold text-gray-600">
                                {squad.player?.full_name?.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() || '??'}
                              </span>
                            )}
                          </div>
                          
                          {/* Player Details */}
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-semibold text-gray-900 truncate">
                              {squad.player?.full_name || 'Unknown Player'}
                            </h3>
                            <div className="flex items-center space-x-3 text-xs text-gray-600">
                              <span>{squad.player?.position || '—'}</span>
                              <span>{squad.player?.nationality || '—'}</span>
                              {squad.player?.kit_number && (
                                <span>#{squad.player.kit_number}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Tournament & Season Info - 3 columns */}
                        <div className="col-span-3 text-center">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {squad.tournament?.name || 'Unknown Tournament'}
                          </div>
                          <div className="text-xs text-gray-600">
                            {squad.season?.name || 'Unknown Season'}
                          </div>
                        </div>

                        {/* Active Status Toggle - 3 columns */}
                        <div className="col-span-3 flex items-center justify-end space-x-3">
                          <span className="text-sm text-gray-600">
                            {squad.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            onClick={() => handleActiveToggle(squad.id, squad.is_active)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              squad.is_active ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                squad.is_active ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {getFilteredEditSquadData().length === 0 && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Squad Data Found</h3>
                      <p className="text-sm text-gray-500">Try adjusting your filters to see squad data.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {pendingChanges.size > 0 && (
                    <span>{pendingChanges.size} change{pendingChanges.size !== 1 ? 's' : ''} pending</span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCloseEditModal}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEditChanges}
                    disabled={pendingChanges.size === 0}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TeamSquadPage;